import * as React from 'react';
import * as styles from './build-segment.module.scss';
import { CircularProgress, MenuItem, Select } from '@material-ui/core';
import { useSegmentAttributeDetails } from '../../../hooks/useSegmentAttributeDetails';
import {
  SegmentAttributeDatetime,
  SegmentAttributeFilterRender,
  SegmentAttributeOptions,
  SegmentAttributeRange,
  SegmentAttributeTransactional,
  SelectedAttribute,
  SelectedAttributeDaterange,
  SelectedAttributeDaysRange,
  SelectedAttributeInput,
  SelectedAttributeOption,
  SelectedAttributeRange,
  SelectedAttributeTransactional
} from '../../../models/segments';
import AttributeFilterDatetime from './attribute-filter-datetime';
import AttributeFilterOptions from './attribute-filter-options';
import AttributeFilterRange from './attribute-filter-range';
import AttributeFilterInput from './attribute-filter-input';
import { evalAttributeFilterRenderType } from '../../../utilities/attribute-filter';
import AttributeFilterTransactional from './attribute-filter-transactional';

interface Props {
  selectedAttribute?: SelectedAttribute;
  position: number[];
  exclude?: boolean;
  attributeIndex: number;
}

/**
 * Attribute Filter - based on the type of the selected attribute's value, render the filter select menu
 */
const AttributeFilter = ({ selectedAttribute, position, exclude, attributeIndex }: Props) => {
  let attributeDetailsQuery: any = useSegmentAttributeDetails(
    selectedAttribute?.attributeId,
    selectedAttribute != undefined,
    selectedAttribute?.isTransactional
  );

  const renderEmpty = () => <Select id="filter-attribute-0" value="" disabled />;

  const renderLoading = () => (
    <Select
      id={`filter-attribute-${selectedAttribute.attributeId}`}
      value="loading"
      disabled
      renderValue={() => (
        <div className={styles.filterSelectLoading}>
          <CircularProgress size="22px" />
        </div>
      )}
    >
      <MenuItem value="loading">Loading</MenuItem>
    </Select>
  );

  const renderError = () => (
    <Select
      id={`filter-attribute-${selectedAttribute.attributeId}`}
      className={styles.filterError}
      value="error"
      disabled
    >
      <MenuItem value="error">Error: {attributeDetailsQuery.error.message}</MenuItem>
    </Select>
  );

  const renderRange = (attributeRange: SegmentAttributeRange, sa: SelectedAttributeRange) => (
    <AttributeFilterRange
      attribute={attributeRange}
      selectedAttribute={sa}
      position={position}
      exclude={exclude}
      attributeIndex={attributeIndex}
    />
  );

  const renderDatetime = (
    attributeDatetime: SegmentAttributeDatetime,
    sa: SelectedAttributeDaterange | SelectedAttributeDaysRange
  ) => (
    <AttributeFilterDatetime
      attribute={attributeDatetime}
      selectedAttribute={sa}
      position={position}
      exclude={exclude}
      attributeIndex={attributeIndex}
    />
  );

  const renderInput = (attributeOptions: SegmentAttributeOptions, sa: SelectedAttribute) => (
    <AttributeFilterInput
      attribute={attributeOptions}
      selectedAttribute={sa as SelectedAttributeInput}
      position={position}
      exclude={exclude}
      attributeIndex={attributeIndex}
    />
  );

  const renderTransactional = (
    attributeTransactional: SegmentAttributeTransactional,
    sa: SelectedAttributeTransactional
  ) => (
    <AttributeFilterTransactional
      attribute={attributeTransactional}
      selectedAttribute={sa}
      position={position}
      exclude={exclude}
      attributeIndex={attributeIndex}
    />
  );

  const renderOptions = (attributeOptions: SegmentAttributeOptions, sa: SelectedAttributeOption) => (
    <AttributeFilterOptions
      attribute={attributeOptions}
      selectedAttribute={sa}
      position={position}
      exclude={exclude}
      attributeIndex={attributeIndex}
    />
  );

  const renderAttribute = () => {
    const attributeRangeFilter = evalAttributeFilterRenderType(attributeDetailsQuery.data, selectedAttribute);
    switch (attributeRangeFilter) {
      case SegmentAttributeFilterRender.Input:
        return renderInput(attributeDetailsQuery.data as SegmentAttributeOptions, selectedAttribute);
      case SegmentAttributeFilterRender.Options:
        return renderOptions(
          attributeDetailsQuery.data as SegmentAttributeOptions,
          selectedAttribute as SelectedAttributeOption
        );
      case SegmentAttributeFilterRender.Range:
        return renderRange(
          attributeDetailsQuery.data as SegmentAttributeRange,
          selectedAttribute as SelectedAttributeRange
        );
      case SegmentAttributeFilterRender.Datetime:
        return renderDatetime(
          attributeDetailsQuery.data as SegmentAttributeDatetime,
          selectedAttribute as SelectedAttributeDaterange | SelectedAttributeDaysRange
        );
      case SegmentAttributeFilterRender.Transactional:
        return renderTransactional(
          attributeDetailsQuery.data as SegmentAttributeTransactional,
          selectedAttribute as SelectedAttributeTransactional
        );
      default:
        return <></>;
    }
  };

  /** New segment with no attribute selected */
  if (!selectedAttribute) {
    return renderEmpty();
  }

  /** Attribute selected and available filters loading */
  if (attributeDetailsQuery.isLoading) {
    return renderLoading();
  }

  /** Attribute filters fail to load */
  if (attributeDetailsQuery.isError) {
    return renderError();
  }

  return renderAttribute();
};

export default AttributeFilter;
